import React, { useEffect, useState, Suspense, lazy, createElement } from 'react';
import { Helmet } from 'react-helmet-async';
import { TopBanner } from '../components/TopBanner';
import { Header } from '../components/Header';
import { Hero } from '../components/Hero';
import { Process } from '../components/Process';
// Lazy load below-fold sections for better initial load performance
const Features = lazy(() => import('../components/Features').then(m => ({
  default: m.Features
})));
const Pricing = lazy(() => import('../components/Pricing').then(m => ({
  default: m.Pricing
})));
const FAQ = lazy(() => import('../components/FAQ').then(m => ({
  default: m.FAQ
})));
const Contact = lazy(() => import('../components/Contact').then(m => ({
  default: m.Contact
})));
const Footer = lazy(() => import('../components/Footer').then(m => ({
  default: m.Footer
})));
// Lazy load modal components (only needed when opened)
const HubSpotForm = lazy(() => import('../components/HubSpotForm').then(m => ({
  default: m.HubSpotForm
})));
const TimedPopup = lazy(() => import('../components/TimedPopup').then(m => ({
  default: m.TimedPopup
})));
import { Modal } from '../components/ui/Modal';
export function HomePage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const seoTitle = 'Caravan & Boat Storage Perth | Hold My Van';
  const seoDescription = 'Looking for caravan or boat storage near Perth? Hold My Van offers practical, secure storage. Register for priority access today.';
  const seoImage = 'https://cdn.magicpatterns.com/uploads/4H7vMpv3GeDGEYCVvASyPc/Hold_my_van_hero.jpg';
  const siteUrl = 'https://holdmyvan.com.au';
  // Google Analytics 4 - Deferred loading for better performance
  useEffect(() => {
    // Defer GA loading until after page is interactive
    const loadGA = () => {
      const existingScript = document.querySelector('script[src*="googletagmanager.com/gtag/js?id=G-P90L0PE259"]');
      if (existingScript) {
        return;
      }
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-P90L0PE259';
      document.head.appendChild(script);
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      gtag('js', new Date());
      gtag('config', 'G-P90L0PE259');
      window.gtag = gtag;
    };
    // Load GA after page is interactive (requestIdleCallback or setTimeout fallback)
    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadGA);
    } else {
      setTimeout(loadGA, 1000);
    }
  }, []);
  // Schema Markup
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Hold My Van',
    description: "Caravan and boat storage in Perth's northern corridor",
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Perth Northern Corridor',
      addressRegion: 'Western Australia',
      addressCountry: 'AU'
    },
    priceRange: '$$',
    areaServed: 'Perth, Western Australia',
    url: siteUrl,
    image: seoImage
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [{
      '@type': 'Question',
      name: 'What sizes of caravans, boats and motorhomes do you accommodate?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Our standard bays are 9 meters long by 3 meters wide. This covers most single and dual axle caravans, camper trailers, boats and motorhomes. If you have a larger vehicle, please contact us to discuss options.'
      }
    }, {
      '@type': 'Question',
      name: 'What security measures are in place?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Security is our top priority. Our facility will feature 24/7 video security camera monitoring and secure gated access. We're currently evaluating sites and looking at options with residential caretakers living on site for added security and peace of mind."
      }
    }, {
      '@type': 'Question',
      name: 'Can I access my caravan, boat or motorhome at any time?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We plan to offer convenient access hours from 7:00 AM to 7:00 PM, seven days a week. 24/7 access may be available upon request. Final access hours will be confirmed once our site is secured.'
      }
    }, {
      '@type': 'Question',
      name: 'Do I need to have insurance for my vehicle?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, we require all stored vehicles to have comprehensive insurance. While our facility is secure and monitored, we require you to maintain your own insurance coverage for complete peace of mind.'
      }
    }, {
      '@type': 'Question',
      name: 'Are there any long-term contracts?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No! We offer flexible month-by-month terms with no long-term commitments required. This gives you the freedom to store your caravan or boat for as long or as short as you need.'
      }
    }, {
      '@type': 'Question',
      name: 'Why should I join the priority list now?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Storage capacity will be limited at launch. Priority list members receive first access to available bays and early bird pricing before we open to the wider public.'
      }
    }, {
      '@type': 'Question',
      name: 'Am I committed if I join the priority list?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Not at all! Registering is completely free and carries no obligation. It simply adds you to our priority waitlist so you'll be among the first to hear about our opening and secure early bird pricing. You can decide whether to proceed when we contact you with final details."
      }
    }, {
      '@type': 'Question',
      name: 'When will the facility open?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "We're currently evaluating several sites within 35kms of Perth CBD. Once we secure the perfect location, we'll notify all registered customers with an opening timeline. Early bird registrations will receive priority access."
      }
    }, {
      '@type': 'Question',
      name: 'Where will the site be located?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Our first site will be located within Perth's northern corridor. We've already assessed several suitable properties and are currently focusing on options in Neerabup and Bullsbrook."
      }
    }, {
      '@type': 'Question',
      name: 'What happens after I register my interest?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "We'll be in touch as we get closer to our opening date with site location and details."
      }
    }]
  };
  return <div className="min-h-screen flex flex-col font-sans" data-id="element-800">
      <Helmet data-id="element-801">
        {/* Preconnect to external domains for faster resource loading */}
        <link rel="preconnect" href="https://fonts.googleapis.com" data-id="element-802" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" data-id="element-803" />
        <link rel="preconnect" href="https://cdn.magicpatterns.com" data-id="element-804" />

        {/* Preload critical hero image for LCP optimization */}
        <link rel="preload" as="image" href="https://cdn.magicpatterns.com/uploads/4H7vMpv3GeDGEYCVvASyPc/Hold_my_van_hero.jpg" fetchPriority="high" data-id="element-805" />

        {/* Basic SEO */}
        <title data-id="element-806">{seoTitle}</title>
        <meta name="description" content={seoDescription} data-id="element-807" />
        <meta name="keywords" content="caravan storage perth, boat storage perth, caravan storage near perth, caravan storage northern suburbs perth, motorhome storage perth, secure caravan storage, caravan storage bullsbrook, caravan storage neerabup" data-id="element-808" />
        <link rel="canonical" href={siteUrl} data-id="element-809" />
        <meta name="robots" content="index, follow" data-id="element-810" />
        <meta name="author" content="Hold My Van" data-id="element-811" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" data-id="element-812" />
        <meta property="og:url" content={siteUrl} data-id="element-813" />
        <meta property="og:title" content={seoTitle} data-id="element-814" />
        <meta property="og:description" content={seoDescription} data-id="element-815" />
        <meta property="og:image" content={seoImage} data-id="element-816" />
        <meta property="og:site_name" content="Hold My Van" data-id="element-817" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-818" />
        <meta name="twitter:title" content={seoTitle} data-id="element-819" />
        <meta name="twitter:description" content={seoDescription} data-id="element-820" />
        <meta name="twitter:image" content={seoImage} data-id="element-821" />

        {/* Schema Markup */}
        <script type="application/ld+json" data-id="element-822">
          {JSON.stringify(localBusinessSchema)}
        </script>
        <script type="application/ld+json" data-id="element-823">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <TopBanner data-id="element-824" />
      <Header onJoinClick={openModal} data-id="element-825" />
      <main className="flex-grow" data-id="element-826">
        <Hero onJoinClick={openModal} data-id="element-827" />
        <Process onJoinClick={openModal} data-id="element-828" />

        {/* Lazy load below-fold sections with minimal loading UI */}
        <Suspense fallback={<div className="h-20" data-id="element-830" />} data-id="element-829">
          <Features data-id="element-831" />
        </Suspense>

        <Suspense fallback={<div className="h-20" data-id="element-833" />} data-id="element-832">
          <Pricing onJoinClick={openModal} data-id="element-834" />
        </Suspense>

        <Suspense fallback={<div className="h-20" data-id="element-836" />} data-id="element-835">
          <FAQ data-id="element-837" />
        </Suspense>

        <Suspense fallback={<div className="h-20" data-id="element-839" />} data-id="element-838">
          <Contact onJoinClick={openModal} data-id="element-840" />
        </Suspense>
      </main>

      <Suspense fallback={<div className="h-20" data-id="element-842" />} data-id="element-841">
        <Footer data-id="element-843" />
      </Suspense>

      <Modal isOpen={isModalOpen} onClose={closeModal} data-id="element-844">
        <div className="text-center mb-6" data-id="element-845">
          <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-846">
            Join the Priority List
          </h3>
          <p className="text-gray-600 mb-2" data-id="element-847">
            Early registrants will receive priority access to available storage
            bays and introductory pricing. Lock in early bird pricing.
          </p>
          <p className="text-sm text-gray-500 italic" data-id="element-848">
            Registering your interest does not commit you to a booking.
          </p>
        </div>
        {/* Lazy load HubSpot form only when modal opens */}
        <Suspense fallback={<div className="h-40 flex items-center justify-center" data-id="element-850">
              <div className="text-gray-500" data-id="element-851">Loading form...</div>
            </div>} data-id="element-849">
          <HubSpotForm data-id="element-852" />
        </Suspense>
      </Modal>

      {/* Lazy load timed popup (has 10 second delay anyway) */}
      <Suspense fallback={null} data-id="element-853">
        <TimedPopup delaySeconds={10} data-id="element-854" />
      </Suspense>
    </div>;
}